// auditDisplay — the audit vocabulary's shared display layer: the ONE place that
// maps an audit row to its presentation. Adding a new action or entity is a row
// here (+ an i18n key); the consumers never change (the "evolve right" contract).
//
//  • actionMeta(action) → glyph + tone. Icons by CONCEPT (ADR-042), reusing
//    existing registry concepts; an unknown/rare action falls back gracefully.
//  • entityMeta(type)   → glyph + the PeekHost collection IFF a peek exists
//    (role/zone/cluster). Entities without a peek render as plain text — no dead
//    link (only peek what actually has a peek).
//  • renderValue / fieldLabel → diff values reuse the ENTITY's existing i18n keys
//    (the audit surface shares the translation surface, mints no value keys);
//    scalars show as-stored (forensic). The (entity_type, field) map extends per
//    entity.
//  • diffFields → the union-of-keys diff over an `edited` row's two snapshots.
//
// It lives in `patterns/` because TWO surfaces read the same trail: the platform
// audit page (the one stream, rendered as chips on the feed Timeline) and an
// entity's own "Changes" door (the same rows, rendered as axis lines on
// EntityTimeline — FRONTEND-PATTERNS §Entity surfaces). No JSX here on purpose:
// each surface owns its own render, this owns the vocabulary.

import type { TFunction } from 'i18next';
import type { AuditEvent } from '@ems-app/schemas/audit/responses';
import { icons, type Icon } from '../ui/icons.js';

export type Tone = 'add' | 'remove' | 'caution' | 'neutral';

interface ActionMeta {
  icon: Icon;
  tone: Tone;
}
const ACTION_META: Record<string, ActionMeta> = {
  create: { icon: icons.add, tone: 'add' },
  update: { icon: icons.edit, tone: 'neutral' },
  delete: { icon: icons.delete, tone: 'remove' },
  clone: { icon: icons.copy, tone: 'neutral' },
  reactivate: { icon: icons.restore, tone: 'add' },
  activate: { icon: icons.restore, tone: 'add' },
  disable: { icon: icons.statusPaused, tone: 'caution' },
  decommission: { icon: icons.statusBlocked, tone: 'caution' },
  associate: { icon: icons.add, tone: 'neutral' },
  remove_from_project: { icon: icons.delete, tone: 'remove' },
  handover: { icon: icons.handover, tone: 'neutral' },
  assign: { icon: icons.add, tone: 'neutral' },
  revoke: { icon: icons.statusBlocked, tone: 'caution' },
  export: { icon: icons.export, tone: 'neutral' },
  import_started: { icon: icons.upload, tone: 'neutral' },
  import_completed: { icon: icons.upload, tone: 'add' },
  import_failed: { icon: icons.statusError, tone: 'remove' },
  login: { icon: icons.user, tone: 'neutral' },
  logout: { icon: icons.signOut, tone: 'neutral' },
  login_fail: { icon: icons.statusError, tone: 'remove' },
  account_locked: { icon: icons.statusBlocked, tone: 'caution' },
  password_changed: { icon: icons.statusBlocked, tone: 'neutral' },
  mfa_enrolled: { icon: icons.statusBlocked, tone: 'add' },
  file_downloaded: { icon: icons.download, tone: 'neutral' },
  file_previewed: { icon: icons.reveal, tone: 'neutral' },
  file_classified: { icon: icons.check, tone: 'add' },
  file_moved: { icon: icons.folder, tone: 'neutral' },
  file_replaced: { icon: icons.upload, tone: 'neutral' },
  file_restored: { icon: icons.restore, tone: 'add' },
};
const ACTION_FALLBACK: ActionMeta = { icon: icons.history, tone: 'neutral' };
export const actionMeta = (action: string): ActionMeta => ACTION_META[action] ?? ACTION_FALLBACK;

interface EntityMeta {
  icon: Icon;
  /** PeekHost collection prefix — present ONLY where a peek exists. */
  peek?: 'roles' | 'zones' | 'clusters' | 'users' | 'assets';
}
const ENTITY_META: Record<string, EntityMeta> = {
  asset: { icon: icons.asset, peek: 'assets' },
  asset_model: { icon: icons.powerSystem },
  asset_owner: { icon: icons.assetOwner },
  role: { icon: icons.role, peek: 'roles' },
  zone: { icon: icons.zone, peek: 'zones' },
  cluster: { icon: icons.cluster, peek: 'clusters' },
  site_type: { icon: icons.site },
  responsibility: { icon: icons.responsibilities },
  user: { icon: icons.users, peek: 'users' },
  auth: { icon: icons.user },
  site: { icon: icons.site },
  config: { icon: icons.contract },
  integration: { icon: icons.telegram },
  file: { icon: icons.file },
};
const ENTITY_FALLBACK: EntityMeta = { icon: icons.history };
export const entityMeta = (type: string): EntityMeta => ENTITY_META[type] ?? ENTITY_FALLBACK;

export const TONE_TEXT: Record<Tone, string> = {
  add: 'text-[var(--color-success)]',
  remove: 'text-[var(--color-danger)]',
  caution: 'text-[var(--color-warning)]',
  neutral: 'text-[var(--color-text-secondary)]',
};

// ── value rendering ───────────────────────────────────────────────────────────
// Coded values reuse the entity's EXISTING namespace keys (bundled, so the
// colon-form t() resolves). Extend per (entity_type, field) as entities land.
const CODED: Record<string, (v: string, t: TFunction) => string> = {
  'responsibility.level': (v, t) => t(`responsibilities:level.${v}`, { defaultValue: v }),
  'asset.condition': (v, t) => t(`assets:condition.${v}`, { defaultValue: v }),
  'asset.acquisition_mode': (v, t) => t(`assets:acquisitionMode.${v}`, { defaultValue: v }),
  'asset.acquired_condition': (v, t) => t(`assets:acquiredAs.${v}`, { defaultValue: v }),
  'asset.rating_unit': (v, t) => t(`assets:ratingUnit.${v}`, { defaultValue: v }),
  // The access facet audits per-field: the non-secret coordinates (device_ip · the
  // via ref · the `<kind>.<field>` service/SIM fields) show real VALUES; only the
  // actual secret fields carry a `set/updated/cleared` marker (backend
  // deviceAccessAuditPair — a credential value never enters audit_logs).
};

function humanize(field: string): string {
  // dots too, so the access facet's per-field keys (`web.port`, `cellular.sim1.imsi`)
  // read as "Web port" / "Cellular sim1 imsi" without a per-key label.
  const s = field.replace(/[_.]/g, ' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Field label — reuse an audit `field.*` key, else humanize the raw column. */
export function fieldLabel(field: string, t: TFunction): string {
  return t(`field.${field}`, { defaultValue: humanize(field) });
}

/** An audit `{id,label}` reference snapshot (ADR-058) → its captured label, or null. */
export function refLabel(value: unknown): string | null {
  if (value && typeof value === 'object' && 'label' in value) {
    const l = (value as { label: unknown }).label;
    return typeof l === 'string' ? l : String(l);
  }
  return null;
}

/** A value QUALIFIED by one reference — `{ <ref>: {id,label}, value }` (a member's flag on the team's row, ADR-058):
 *  the reference's label and the value, or null when the shape is another. */
function qualifiedValue(value: unknown): { label: string; value: unknown } | null {
  if (!value || typeof value !== 'object' || Array.isArray(value) || !('value' in value)) return null;
  const refs = Object.keys(value).filter((k) => k !== 'value');
  if (refs.length !== 1) return null;
  const label = refLabel((value as Record<string, unknown>)[refs[0]!]);
  return label === null ? null : { label, value: (value as { value: unknown }).value };
}

/** Render a stored value for display — a {id,label} ref → its label; a value
 *  qualified by a ref → "label · value"; coded → shared key; scalar/text
 *  as-stored (forensic snapshot); booleans localized; array → comma-joined;
 *  null → em dash. */
export function renderValue(entityType: string, field: string, value: unknown, t: TFunction): string {
  if (value === null || value === undefined) return t('value.none', { defaultValue: '—' });
  // a place or unit the viewer may not see — the server sends its kind alone (an entity's own History; ADR-066 §9)
  if (typeof value === 'object' && (value as { hidden?: unknown }).hidden === true) {
    return t(`value.hidden.${(value as { kind: string }).kind}`, { defaultValue: '—' });
  }
  if (typeof value === 'boolean') return value ? t('value.yes', { ns: 'common', defaultValue: 'Yes' }) : t('value.no', { ns: 'common', defaultValue: 'No' });
  if (typeof value === 'string') {
    const coded = CODED[`${entityType}.${field}`];
    return coded ? coded(value, t) : value;
  }
  if (typeof value === 'number') return String(value);
  const ref = refLabel(value);
  if (ref !== null) return ref;
  const qualified = qualifiedValue(value);
  if (qualified !== null) return `${qualified.label} · ${renderValue(entityType, field, qualified.value, t)}`;
  if (Array.isArray(value)) return value.map((v) => renderValue(entityType, field, v, t)).join(', ');
  return JSON.stringify(value);
}

// Fields that carry the entity's display name (the row headline), not a "change":
// excluded from the diff UNLESS they appear in BOTH blobs (a real rename).
export const LABEL_FIELDS = new Set(['name', 'username']);

// ── the edited-row diff ───────────────────────────────────────────────────────
export interface FieldDiff {
  field: string;
  prev?: unknown;
  next?: unknown;
}

/** Field diffs from an `edited` row's snapshot blobs: the union of keys, minus
 *  the headline name field unless it appears in BOTH (a real rename). */
export function diffFields(ev: Pick<AuditEvent, 'previous_value' | 'new_value'>): FieldDiff[] {
  const prev = (ev.previous_value ?? {}) as Record<string, unknown>;
  const next = (ev.new_value ?? {}) as Record<string, unknown>;
  const keys = new Set<string>([...Object.keys(prev), ...Object.keys(next)]);
  const out: FieldDiff[] = [];
  for (const field of keys) {
    if (LABEL_FIELDS.has(field) && !(field in prev)) continue;
    out.push({ field, prev: prev[field], next: next[field] });
  }
  return out;
}
